import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { ChevronDown, ChevronRight } from "lucide-react";

const TYPE_COLORS = {
    DATABASE_ERROR: "destructive",
    DATABASE_RATE_LIMIT: "destructive",
    UNHANDLED_ERROR: "destructive",
    UNHANDLED_REJECTION: "destructive",
    REACT_ERROR: "destructive",
    code_error: "secondary",
    BUILD_ERROR: "secondary",
};

export default function ErrorRow({ error }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const variant = TYPE_COLORS[error.errorType] || "outline";
    const lastSeen = error.lastSeenAt ? new Date(error.lastSeenAt).toLocaleString() : "-";
    const firstSeen = error.firstSeenAt ? new Date(error.firstSeenAt).toLocaleString() : "-";
    const meta = error.metadata || {};

    return (
        <div className="border rounded-lg p-3">
            <button
                className="w-full flex items-start gap-3 text-left"
                onClick={() => setExpanded(!expanded)}
            >
                {expanded
                    ? <ChevronDown className="h-4 w-4 mt-0.5 shrink-0" />
                    : <ChevronRight className="h-4 w-4 mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={variant}>{error.errorType}</Badge>
                        <span className="text-xs font-medium">{error.occurrenceCount}x</span>
                        <span className="text-xs text-muted-foreground">{lastSeen}</span>
                        {error.workflowName && (
                            <span className="text-xs text-blue-600 dark:text-blue-400 truncate max-w-[200px]">
                                {error.workflowName}
                            </span>
                        )}
                        {error.companyName && (
                            <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                {error.companyName}
                            </span>
                        )}
                    </div>
                    <p className="text-sm mt-1 truncate">{error.errorMessage?.slice(0, 150)}</p>
                </div>
            </button>

            {expanded && <ErrorDetail error={error} meta={meta} firstSeen={firstSeen} t={t} />}
        </div>
    );
}

function ErrorDetail({ error, meta, firstSeen, t }) {
    return (
        <div className="mt-3 ml-7 space-y-3 text-sm">
            <div className="bg-muted rounded p-3 overflow-x-auto">
                <pre className="text-xs whitespace-pre-wrap break-words">{error.errorMessage}</pre>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <DetailField label={t("admin.intelligence.errors.workflow")} value={error.workflowName || error.workflowId} />
                <DetailField label={t("admin.intelligence.errors.step")} value={error.stepId} />
                <DetailField label={t("admin.intelligence.errors.company")} value={error.companyName || error.companyId} />
                <DetailField label={t("admin.intelligence.errors.firstSeen")} value={firstSeen} />
                <DetailField label={t("admin.intelligence.errors.environment")} value={error.environment} />
                {error.url && <DetailField label={t("admin.intelligence.errors.url")} value={error.url} />}
            </div>

            {(meta.apiUrl || meta.httpStatus || meta.httpMethod || meta.operation || meta.tableName) && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {meta.apiUrl && <DetailField label={t("admin.intelligence.errors.apiEndpoint")} value={meta.apiUrl} />}
                    {meta.httpStatus && <DetailField label={t("admin.intelligence.errors.httpStatus")} value={meta.httpStatus} />}
                    {meta.httpMethod && <DetailField label={t("admin.intelligence.errors.httpMethod")} value={meta.httpMethod} />}
                    {meta.operation && <DetailField label={t("admin.intelligence.errors.dbOperation")} value={meta.operation} />}
                    {meta.tableName && <DetailField label={t("admin.intelligence.errors.dbTable")} value={meta.tableName} />}
                    {meta.fetchError && <DetailField label={t("admin.intelligence.errors.fetchError")} value={meta.fetchError} />}
                </div>
            )}

            {(meta.source || meta.lineno || meta.componentStack) && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {meta.source && <DetailField label={t("admin.intelligence.errors.sourceFile")} value={meta.source} />}
                    {meta.lineno && <DetailField label={t("admin.intelligence.errors.line")} value={`${meta.lineno}${meta.colno ? `:${meta.colno}` : ""}`} />}
                </div>
            )}

            {meta.responseBody && (
                <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.errors.responseBody")}
                    </h4>
                    <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded p-3 overflow-x-auto max-h-32 overflow-y-auto">
                        <pre className="text-xs whitespace-pre-wrap break-words">{meta.responseBody}</pre>
                    </div>
                </div>
            )}

            {meta.buildOutput && (
                <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.errors.buildOutput")}
                    </h4>
                    <div className="bg-muted rounded p-3 overflow-x-auto">
                        <pre className="text-xs whitespace-pre-wrap break-words">{meta.buildOutput}</pre>
                    </div>
                </div>
            )}

            {meta.componentStack && (
                <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.errors.componentStack")}
                    </h4>
                    <div className="bg-muted rounded p-3 overflow-x-auto max-h-32 overflow-y-auto">
                        <pre className="text-xs whitespace-pre-wrap break-words">{meta.componentStack}</pre>
                    </div>
                </div>
            )}

            {(meta.retryCount != null || meta.duration != null || meta.userAgent) && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {meta.retryCount != null && <DetailField label={t("admin.intelligence.errors.retries")} value={meta.retryCount} />}
                    {meta.duration != null && <DetailField label={t("admin.intelligence.errors.duration")} value={`${meta.duration}ms`} />}
                    {meta.userAgent && <DetailField label={t("admin.intelligence.errors.userAgent")} value={meta.userAgent} />}
                </div>
            )}

            {meta.stack && (
                <div>
                    <h4 className="text-xs font-medium text-muted-foreground mb-1">
                        {t("admin.intelligence.errors.stackTrace")}
                    </h4>
                    <div className="bg-muted rounded p-3 overflow-x-auto max-h-48 overflow-y-auto">
                        <pre className="text-xs whitespace-pre-wrap break-words">{meta.stack}</pre>
                    </div>
                </div>
            )}
        </div>
    );
}

function DetailField({ label, value }) {
    if (!value || value === "_" || value === "unknown") return null;
    return (
        <div className="bg-muted/50 rounded p-2">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="text-xs font-medium break-all">{value}</div>
        </div>
    );
}
